/**
 * Typography used across the app, independent of `defaultAppTheme.typography`
 * (which is empty). Sourced from GlobalStyle and Google Fonts links.
 */
export const PROJECT_GLOBAL_TYPOGRAPHY = {
  sourceFile: "src/styles/index.ts",
  fontStack: "'Montserrat', sans-serif",
  appliesTo: ["body", "input", "button"],
  baseFontSize: "16px",
  headingStrongWeight: 500,
} as const;

/** Families loaded in `public/index.html` (Google Fonts). */
export const PROJECT_WEB_FONT_FAMILIES = [
  "Montserrat",
  "Plus Jakarta Sans",
  "Mulish",
  "Poppins",
  "Inter",
] as const;

export type ProjectWebFontFamily = (typeof PROJECT_WEB_FONT_FAMILIES)[number];

export const PROJECT_FONT_USAGE: Record<
  ProjectWebFontFamily,
  { summary: string; examples: string[] }
> = {
  Montserrat: {
    summary:
      "Default stack from GlobalStyle; also used in header, legacy Home sections, and several modals.",
    examples: [
      "src/styles/index.ts — body, input, button",
      "src/components/Header/styles.ts",
      "src/pages/Home/MapSection/styles.ts",
    ],
  },
  Inter: {
    summary: "Buttons, property cards, search/list UI, galleries, and many NewHome sections.",
    examples: [
      "src/components/CustomButton/styles.ts",
      "src/components/PropertyCard/style.ts",
      "src/pages/NewHome/components/RentJourneySection/styles.ts",
    ],
  },
  Mulish: {
    summary: "Cards and emphasis blocks on home, property details, and search landing.",
    examples: [
      "src/pages/NewHome/components/AnunciatePropertySection/components/ClientCard/styles.ts",
      "src/pages/PropertyDetails/components/DescriptionTabs/CharacteristicsTab/CharacteristicCard/styles.ts",
      "src/components/SearchBarToHomePage/styles.ts",
    ],
  },
  "Plus Jakarta Sans": {
    summary: "List titles, filters, property list cards, and parts of NewHome.",
    examples: [
      "src/pages/PropertiesList/components/PropertyCardList/style.ts",
      "src/pages/PropertiesList/components/TitleSearch/SearchFilter/styles.ts",
      "src/pages/NewHome/components/MapSection/styles.ts",
    ],
  },
  Poppins: {
    summary: "Select marketing sections on NewHome (not the global default).",
    examples: [
      "src/pages/NewHome/components/MapSection/styles.ts",
      "src/pages/NewHome/components/AdvantagesSection/styles.ts",
    ],
  },
};

export const PROJECT_TYPOGRAPHY_DOCUMENTATION = {
  fontLinksHtml: "public/index.html",
  global: PROJECT_GLOBAL_TYPOGRAPHY,
  googleFontFamilies: [...PROJECT_WEB_FONT_FAMILIES],
  usageByFamily: PROJECT_FONT_USAGE,
} as const;
